<template>
    <div class="info-card details-card painel-destinatarios" v-if="gruposVisiveis.length">
        <div class="card-title">Destinatários por etapa</div>
        <div
            v-for="grupo in gruposVisiveis"
            :key="grupo.cd"
            class="env-grupo"
            :class="{ 'env-grupo--ativa': grupo.situacao === 'ativa' }">
            <div class="env-grupo-cabecalho">
                <span class="env-grupo-titulo" :class="grupo.classe">{{ grupo.titulo }}</span>
                <span
                    class="env-grupo-situacao"
                    :class="`env-grupo-situacao--${grupo.situacao}`">
                    {{ labelSituacaoEtapa(grupo.situacao) }}
                </span>
            </div>

            <div v-if="grupo.itens.length" class="env-subsecao">
                <span class="env-subsecao-label">Designados</span>
                <ul class="envolvidos-list mb-0">
                    <li
                        v-for="item in grupo.itens"
                        :key="envolvidoKey(item)">
                        <span v-if="item.tpResponsavel === 'U'" class="env-item-usuario">
                            {{ item.nmUsuario || item.dsLogin }}
                        </span>
                        <span v-else class="env-item-setor">
                            Setor: {{ item.nmSetor }}
                        </span>
                    </li>
                </ul>
            </div>

            <div
                v-if="grupo.executor"
                class="env-executor"
                :class="{ 'env-executor--reprovado': grupo.executorLabel === 'Reprovado por' }">
                <span class="env-executor-label">{{ grupo.executorLabel }}</span>
                <span class="env-executor-nome">
                    {{ grupo.executor.nmUsuario || grupo.executor.dsLogin }}
                </span>
                <span v-if="grupo.executor.dtInclusao" class="env-executor-data">
                    {{ formatarData(grupo.executor.dtInclusao) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import { urlApi } from '@/config/global'

    const DEFINICOES_ETAPA = [
        { cd: 1, titulo: 'Revisão', classe: 'env-grupo-titulo--revisao' },
        { cd: 2, titulo: 'Aprovação', classe: 'env-grupo-titulo--aprovacao' },
    ]

    export default {
        name: 'PainelDestinatariosEtapa',
        props: {
            idProcessoArtefato: { type: [Number, String], default: null },
            idProcesso: { type: [Number, String], default: null },
            idProcessoModeloArtefato: { type: [Number, String], default: null },
            cdStatusAtual: { type: [Number, String], default: null },
            nextStep: { type: String, default: null },
            modo: {
                type: String,
                default: 'owner',
                validator: v => ['owner', 'revisao', 'aprovacao'].includes(v),
            },
        },
        data() {
            return {
                envolvidosAtivos: [],
                statusHistorico: [],
                carregando: false,
            }
        },
        computed: {
            cdStatusNormalizado() {
                return String(this.cdStatusAtual || '').trim()
            },

            cdTipoEnvolvidoEtapaAtiva() {
                const cd = this.cdStatusNormalizado
                if (cd === '3') return 1
                if (cd === '5') return 2
                if (['4', '9'].includes(cd) && this.nextStep === 'aprovacao') return 2
                return null
            },

            executoresHistorico() {
                const latest = (codigo) => {
                    const rows = (this.statusHistorico || []).filter(
                        h => String(h.cdTipoStatusHistorico || '').trim() === codigo
                    )
                    if (!rows.length) return null
                    return [...rows].sort(
                        (a, b) => new Date(b.dtInclusao) - new Date(a.dtInclusao)
                    )[0]
                }
                return {
                    revisado: latest('4'),
                    aprovado: latest('6'),
                    reprovado: latest('9'),
                }
            },

            gruposCompletos() {
                const lista = this.envolvidosAtivos || []
                const cdAtivo = this.cdTipoEnvolvidoEtapaAtiva
                const cdStatus = this.cdStatusNormalizado
                const exec = this.executoresHistorico

                return DEFINICOES_ETAPA.map(def => {
                    const itens = lista.filter(e => Number(e.cdTipoEnvolvido) === def.cd)
                    let situacao = 'registrada'
                    if (cdAtivo === def.cd) {
                        situacao = 'ativa'
                    } else if (def.cd === 1 && ['4', '5', '6', '9'].includes(cdStatus)) {
                        situacao = 'concluida'
                    } else if (def.cd === 2 && (cdStatus === '6' || cdStatus === '9')) {
                        situacao = 'concluida'
                    }

                    let executor = null
                    let executorLabel = null
                    if (def.cd === 1 && exec.revisado) {
                        executor = exec.revisado
                        executorLabel = 'Revisado por'
                    } else if (def.cd === 2) {
                        if (exec.aprovado) {
                            executor = exec.aprovado
                            executorLabel = 'Aprovado por'
                        } else if (exec.reprovado) {
                            executor = exec.reprovado
                            executorLabel = 'Reprovado por'
                        }
                    }

                    return { ...def, itens, situacao, executor, executorLabel }
                }).filter(g => g.itens.length > 0 || g.executor)
            },

            gruposVisiveis() {
                if (this.modo === 'revisao') {
                    return this.gruposCompletos.filter(g => g.cd === 1)
                }
                if (this.modo === 'aprovacao') {
                    return this.gruposCompletos.filter(g => g.cd === 1 || g.cd === 2)
                }
                return this.gruposCompletos
            },
        },
        watch: {
            idProcessoArtefato: { handler: 'carregar', immediate: true },
            idProcesso: 'carregar',
            idProcessoModeloArtefato: 'carregar',
        },
        methods: {
            async carregar() {
                const idArtefato = Number(this.idProcessoArtefato)
                if (!idArtefato) {
                    this.envolvidosAtivos = []
                    this.statusHistorico = []
                    return
                }

                this.carregando = true
                try {
                    const idProcesso = Number(this.idProcesso)
                    const idModelo = Number(this.idProcessoModeloArtefato)
                    const promessas = [
                        axios.get(`${urlApi}/ProcessoArtefatoEnvolvido/GetEnvolvidosAtivos`, {
                            params: { idProcessoArtefato: idArtefato },
                        }),
                    ]

                    if (idProcesso && idModelo) {
                        promessas.push(
                            axios.get(`${urlApi}/ProcessoArtefatoStatusHistorico/GetProcessoArtefatoStatusHistorico`, {
                                params: { idProcesso, idProcessoModeloArtefato: idModelo },
                            })
                        )
                    }

                    const resultados = await Promise.all(promessas)
                    this.envolvidosAtivos = resultados[0]?.data?.data || []

                    if (resultados.length > 1) {
                        this.statusHistorico = resultados[1]?.data?.data || []
                    } else {
                        this.statusHistorico = []
                    }
                } catch {
                    this.envolvidosAtivos = []
                    this.statusHistorico = []
                } finally {
                    this.carregando = false
                }
            },

            refresh() {
                return this.carregar()
            },

            envolvidoKey(item) {
                if (item?.idProcessoArtefatoEnvolvidos) {
                    return `env-${item.idProcessoArtefatoEnvolvidos}`
                }
                const tipo = item?.tpResponsavel === 'U' ? item?.dsLogin : item?.idSetor
                return `${item?.cdTipoEnvolvido}-${item?.tpResponsavel}-${tipo}`
            },

            labelSituacaoEtapa(situacao) {
                switch (situacao) {
                    case 'ativa': return 'Etapa ativa'
                    case 'concluida': return 'Etapa concluída'
                    default: return 'Registrada'
                }
            },

            formatarData(iso) {
                try {
                    return new Date(iso).toLocaleString('pt-BR')
                } catch {
                    return iso
                }
            },
        },
    }
</script>

<style scoped>
    .painel-destinatarios {
        animation-delay: 0.25s;
    }

    .card-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 14px;
    }

    .env-grupo + .env-grupo {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px dashed #dee2e6;
    }

    .env-grupo--ativa {
        padding: 12px;
        border-radius: 10px;
        background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%);
        border: 1px solid rgba(35, 95, 232, 0.15);
    }

    .env-grupo-cabecalho {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 10px;
    }

    .env-grupo-titulo {
        font-size: 0.82rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    .env-grupo-titulo--revisao {
        color: #856404;
    }

    .env-grupo-titulo--aprovacao {
        color: #155724;
    }

    .env-grupo-situacao {
        font-size: 0.72rem;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 999px;
        white-space: nowrap;
    }

    .env-grupo-situacao--ativa {
        background: #235FE8;
        color: #fff;
    }

    .env-grupo-situacao--concluida {
        background: #e9ecef;
        color: #6c757d;
    }

    .env-grupo-situacao--registrada {
        background: #fff3cd;
        color: #856404;
    }

    .env-subsecao {
        margin-bottom: 10px;
    }

    .env-subsecao-label {
        display: block;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        color: #868e96;
        margin-bottom: 6px;
    }

    .envolvidos-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .envolvidos-list li {
        padding: 8px 10px;
        border-radius: 8px;
        background: #fff;
        border: 1px solid #e9ecef;
        font-size: 0.9rem;
    }

    .envolvidos-list li + li {
        margin-top: 6px;
    }

    .env-item-setor {
        color: #495057;
    }

    .env-executor {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 10px 12px;
        border-radius: 8px;
        background: linear-gradient(135deg, #f1f8f4 0%, #e8f5e9 100%);
        border-left: 3px solid #28a745;
        font-size: 0.88rem;
    }

    .env-executor--reprovado {
        background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
        border-left-color: #dc3545;
    }

    .env-grupo--ativa .env-executor {
        background: #fff;
        border-left-color: #235FE8;
    }

    .env-executor-label {
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        color: #6c757d;
    }

    .env-executor-nome {
        font-weight: 600;
        color: #212529;
    }

    .env-executor-data {
        font-size: 0.78rem;
        color: #868e96;
    }

    [data-bs-theme="dark"] .env-grupo + .env-grupo {
        border-top-color: #495057;
    }

    [data-bs-theme="dark"] .env-grupo--ativa {
        background: linear-gradient(135deg, #1a2332 0%, #1e2a3a 100%);
        border-color: rgba(35, 95, 232, 0.35);
    }

    [data-bs-theme="dark"] .envolvidos-list li {
        background: #2b3035;
        border-color: #495057;
        color: #dee2e6;
    }

    [data-bs-theme="dark"] .env-executor {
        background: linear-gradient(135deg, #1e2a24 0%, #243028 100%);
    }

    [data-bs-theme="dark"] .env-grupo--ativa .env-executor {
        background: #2b3035;
    }

    [data-bs-theme="dark"] .env-executor-nome {
        color: #f8f9fa;
    }
</style>
